import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";
import { Button } from "components/Button";
import { LoadingSpinner } from "components/LoadingSpinner";
import type { TableRow } from "components/SortableTable/SortableTable.model";
import { HstProducts } from "constants/hst-constants";
import { modalIds } from "constants/modals-constants";
import { ADMIN_ENVIRONMENT_ROUTES } from "constants/routes";
import { useAdmin } from "hooks/useAdmin";
import { useModal } from "hooks/useModal";
import { observer } from "mobx-react";
import type { AdminProductItem } from "models/product/ProductItem";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CssFlex } from "styles/helpers/layout";
import { DeleteAccountsModal } from "../../../UserToolsPage/components/DeleteAccountsModal/DeleteAccountsModal";
import {
  StyledIcon,
  StyledProduct,
  StyledSortableTable,
  StyledSpinnerWrapper,
  StyledStatusPill,
} from "./UsersTable.styled";

interface UsersTableProps {
  storedParams?: string;
}

const UsersTable: React.FC<UsersTableProps> = observer(({ storedParams }) => {
  const { t } = useTranslation("admin-environment");
  const [accountToDelete, setAccountToDelete] = useState<string[]>([]);
  const { deleteAccounts } = useAdmin();
  const { openModal } = useModal();

  const transformProductName = (productId: number, productName: string) => {
    switch (productId) {
      case HstProducts.DUTCH:
        return "NL";
      case HstProducts.ENGLISH:
        return "EN";
      default:
        return productName || "";
    }
  };

  const getEndLicenseTextFromProducts = (products?: AdminProductItem[]) => {
    if (!products || products.length === 0) {
      return "";
    }

    const dates = products.map((product) => product.validUntilDate);
    const definedDates = dates.filter(
      (date): date is string | Date => date !== null && date !== undefined && date !== "",
    );

    if (definedDates.length === 0) {
      return t("accountsTable.permanentLicense", "Permanent license");
    }

    const timestamps = definedDates
      .map((date) => (date instanceof Date ? date.getTime() : new Date(date).getTime()))
      .filter((time) => !Number.isNaN(time));

    const lastDate = new Date(Math.max(...timestamps));
    return lastDate.toLocaleDateString();
  };

  const headerCells = [
    {
      id: "status",
      title: t("accountsTable.status", "Status"),
      size: "auto",
      sortable: true,
    },
    {
      id: "name",
      title: t("accountsTable.name", "Name"),
      size: "auto",
      sortable: true,
    },
    {
      id: "email",
      title: t("accountsTable.email", "Email"),
      size: "auto",
      sortable: true,
    },
    {
      id: "school",
      title: t("accountsTable.school", "School"),
      size: "auto",
    },
    {
      id: "education",
      title: t("accountsTable.education", "Education"),
      size: "auto",
      sortable: true,
    },
    {
      id: "products",
      title: t("accountsTable.products", "Products"),
      size: "auto",
    },
    {
      id: "endDateLicense",
      title: t("accountsTable.endDateLicense", "End date license"),
      size: "auto",
      sortable: true,
    },
    {
      id: "actions",
      title: t("accountsTable.edit", "Edit"),
      size: "auto",
    },
    {
      id: "delete",
      title: t("accountsTable.delete", "Delete"),
      size: "auto",
    },
  ];
  const { filteredAccounts, loading } = useAdmin();

  const rowData = useMemo<TableRow[]>(
    () =>
      filteredAccounts.data.map((account) => ({
        id: account.id,
        cells: [
          {
            id: `account-${account.id}-status`,
            dataCy: "account-status",
            content: (
              <StyledStatusPill key={account.id} $variant={account.status}>
                {account.status}
              </StyledStatusPill>
            ),
          },
          {
            id: `account-${account.id}-name`,
            dataCy: "account-name",
            content: `${account.fullName}`,
          },
          {
            id: `account-${account.id}-email`,
            content: account.email || "",
          },
          {
            id: `account-${account.id}-school`,
            dataCy: "account-school",
            content: `${account.school || ""}`,
          },
          {
            id: `account-${account.id}-educations`,
            dataCy: "account-education",
            content: `${account?.educations?.join(", ") || ""}`,
          },
          {
            id: `account-${account.id}-product-id`,
            dataCy: "account-product-id",
            content: (
              <div>
                {account.products && account.products.length > 0
                  ? account.products.map((product) => {
                      const isExpired =
                        product.validUntilDate &&
                        new Date(product.validUntilDate).getTime() < Date.now();
                      return (
                        <StyledProduct
                          key={product.id}
                          $isExpired={!!isExpired}
                          data-cy={`product-${product.id}`}
                        >
                          {transformProductName(product.id, product.title as string)}
                        </StyledProduct>
                      );
                    })
                  : t("accountsTable.noProducts", "No products")}
              </div>
            ),
          },
          {
            id: `account-${account.id}-end-license`,
            dataCy: "account-end-license",
            content: getEndLicenseTextFromProducts(account.products),
          },
          {
            id: `account-${account.id}-actions`,
            content: (
              <Link
                aria-label={t("accountsTable.edit", "Edit")}
                data-cy={`account-${account.id}-details`}
                role="button"
                to={buildUrlWithPathParams(ADMIN_ENVIRONMENT_ROUTES.USER_DETAILS_PAGE, {
                  userId: account.id,
                })}
                state={{ params: storedParams }}
              >
                <StyledIcon iconName="pencilIcon" size={1} />
              </Link>
            ),
          },
          {
            id: `account-${account.id}-delete`,
            content: (
              <Button
                aria-label={t("accountsTable.delete", "Delete")}
                data-cy={`account-${account.id}-delete`}
                role="button"
                onClick={() => {
                  if (!account.email) return;
                  setAccountToDelete([account.email]);
                  openModal(modalIds.deleteAccountsModal);
                }}
              >
                <StyledIcon iconName="trashIcon" size={1} />
              </Button>
            ),
          },
        ],
      })),
    [filteredAccounts, t, storedParams],
  );

  return (
    <CssFlex flexDirection="column" marginTop="2rem">
      {loading ? (
        <StyledSpinnerWrapper>
          <LoadingSpinner />
        </StyledSpinnerWrapper>
      ) : (
        <StyledSortableTable headerCells={headerCells} rows={rowData} />
      )}
      <DeleteAccountsModal
        emails={accountToDelete}
        onSubmit={() => accountToDelete.length > 0 && deleteAccounts(accountToDelete)}
      />
    </CssFlex>
  );
});

export default UsersTable;
